import { useNavigate } from "react-router-dom";
import { useTranslations } from "next-intl";
import {
  Badge,
  FilterSearch,
  FiltersToolbar,
  formatDateTimeVn,
} from "@chotca/ui";
import { Eye, Mail, Users } from "lucide-react";
import { useAdminShadowOrgsWithApplicants } from "@chotca/api-client";
import type { AdminShadowOrgWithApplicantsItem } from "@chotca/config";
import { useTableFilters } from "@/lib/use-table-filters";
import {
  DataTable,
  usePagination,
  type Column,
  type RowAction,
} from "@/components/data-table";

type Row = AdminShadowOrgWithApplicantsItem;

export function ShadowOrgsClient() {
  const t = useTranslations("admin");
  const nav = useNavigate();
  const filters = useTableFilters({ search: { debounce: 300 } });
  const { page, pageSize, setPage, setPageSize } = usePagination();
  const { data, isLoading, isFetching } = useAdminShadowOrgsWithApplicants({
    search: filters.applied.search,
    page,
    limit: pageSize,
  });

  const list = data?.items ?? [];

  const columns: Column<Row>[] = [
    {
      key: "org",
      header: t("shadowOrgColOrg"),
      cell: (o) => (
        <div className="flex flex-col">
          <span className="flex items-center gap-2 font-semibold text-ink">
            {o.name}
            {o.hasPendingClaimInvitation ? (
              <Badge tone="sun">{t("shadowOrgClaimPending")}</Badge>
            ) : null}
          </span>
          <span className="text-[11px] text-ash">{o.businessType}</span>
        </div>
      ),
    },
    {
      key: "area",
      header: t("shadowOrgColArea"),
      cell: (o) => (
        <span className="text-xs">{o.district || o.city || "—"}</span>
      ),
    },
    {
      key: "postings",
      header: t("shadowOrgColPostings"),
      align: "right",
      cell: (o) => <span className="font-semibold">{o.postingCount}</span>,
    },
    {
      key: "pending",
      header: t("shadowOrgColPendingApplicants"),
      align: "right",
      cell: (o) => <Badge tone="lime-soft">{o.pendingApplicantCount}</Badge>,
    },
    {
      key: "latest",
      header: t("shadowOrgColLatestApplied"),
      cell: (o) => (
        <span className="whitespace-nowrap text-xs text-ash">
          {formatDateTimeVn(new Date(o.latestAppliedAt))}
        </span>
      ),
    },
  ];

  const rowActions: RowAction<Row>[] = [
    {
      key: "view",
      label: t("shadowOrgViewApplicants"),
      icon: Eye,
      onClick: (o) => nav(`/orgs/${o.orgId}`),
    },
    {
      key: "actAs",
      label: t("shadowOrgActAs"),
      icon: Users,
      onClick: (o) => nav(`/orgs/${o.orgId}/act-as`),
    },
    {
      key: "invite",
      label: t("shadowOrgClaimPending"),
      icon: Mail,
      onClick: (o) => nav(`/orgs/${o.orgId}?tab=invitations`),
    },
  ];

  return (
    <div className="space-y-4">
      <header className="space-y-1">
        <h1 className="text-2xl font-bold text-ink">{t("shadowOrgTitle")}</h1>
        <p className="max-w-2xl text-sm text-ash">
          {t("shadowOrgDescription")}
        </p>
      </header>

      <FiltersToolbar
        hasActiveFilters={filters.hasAny}
        onClearAll={filters.clear}
      >
        <FilterSearch
          value={filters.values.search ?? ""}
          onChange={(v) => filters.set("search", v)}
          placeholder={t("shadowOrgSearchPlaceholder")}
        />
      </FiltersToolbar>

      <DataTable
        columns={columns}
        data={list}
        isLoading={isLoading}
        isFetching={isFetching}
        rowKey={(o) => o.orgId}
        rowActions={rowActions}
        onRowClick={(o) => nav(`/orgs/${o.orgId}`)}
        emptyText={t("shadowOrgEmpty")}
        page={page}
        pageSize={pageSize}
        total={data?.total}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
      />
    </div>
  );
}
